import { useState } from "react"
import styled from "styled-components"

import {
  Banner,
  Button,
  Skeleton,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"

import { SearchFilter } from "src/components/Filter/SearchFilter"
import { HomeNamesTooltip } from "src/components/MinutAI/MinutAIKnowledge/MinutAIContentLibrary/HomeNamesTooltip"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchPropertyContexts } from "src/data/propertyContext/propertyContextQueries"
import { TPropertyContext } from "src/data/propertyContext/propertyContextTypes"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { GridTable } from "src/ui/GridTable/GridTable"
import { MoreButton } from "src/ui/GridTable/MoreButton"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import { formatDate } from "src/utils/l10n"

export function MinutAIContentLibrary() {
  const { t, tPlaceholder, langKeys } = useTranslate()

  const { org } = useOrganization()
  const user = useGetUser()

  const [search, setSearch] = useState("")

  const fetchPropertyContexts = useFetchPropertyContexts({
    orgId: org.id,
  })

  const tableColumns: TableColumn<TPropertyContext>[] = [
    {
      value: "title",
      label: tPlaceholder("Title"),
      renderCell: (data) => <Text variant="subtitle">{data.title}</Text>,
      columnWidth: "1fr",
    },
    {
      value: "updated_at",
      label: tPlaceholder("Updated at"),
      renderCell: (data) => (
        <Text>
          {formatDate({
            date: data.updated_at,
            clockType: user.clock_type,
          })}
        </Text>
      ),
      columnWidth: "auto",
    },
    {
      value: "applies_to",
      label: tPlaceholder("Applies to"),
      renderCell: (data) => (
        <HomeNamesTooltip homeIds={data.home_ids ?? []}>
          {t(langKeys.home_count, {
            count: data.home_ids?.length,
          })}
        </HomeNamesTooltip>
      ),
      columnWidth: "auto",
    },
    {
      value: "actions",
      label: "",
      renderCell: () => (
        <AlignRight>
          <MoreButton
            onEdit={() => alert("Not implemented")}
            onDelete={() => alert("Not implemented")}
          />
        </AlignRight>
      ),
      columnWidth: "auto",
    },
  ]

  const { headerElements, rows, templateColumns } = useTableColumns({
    columns: tableColumns,
    data: fetchPropertyContexts.data?.property_contexts,
  })

  if (fetchPropertyContexts.isLoading) {
    return (
      <div>
        <Skeleton width="40ch" />
        <Skeleton width="30ch" />
        <Skeleton width="30ch" />
      </div>
    )
  }

  if (fetchPropertyContexts.isError) {
    return (
      <Banner type="error" fullWidth>
        {t(langKeys.failed_contact_support)}
      </Banner>
    )
  }

  return (
    <div>
      <Header>
        <SearchFilter
          initialValue={search}
          onChange={setSearch}
          placeholder={tPlaceholder("Search for content")}
        />

        <Button onClick={() => alert("Not implemented")} size="small">
          {tPlaceholder("Add content")}
        </Button>
      </Header>

      <GridTable
        header={headerElements}
        rows={rows}
        mobileRows={rows}
        minWidth={0}
        templateColumns={templateColumns}
        horizontalScroll
      />
    </div>
  )
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.L};
`

const AlignRight = styled.div`
  display: flex;
  justify-content: flex-end;
`
